import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import useMotionPreference from "@/hooks/useMotionPreference";
import useScrollStage from "@/hooks/useScrollStage";
import { Counter } from "@/lib/motion";
import { getStats } from "@/lib/api";
import { Rocket, GitCommit, Code2, Layers, Briefcase, GraduationCap } from "lucide-react";

const FALLBACK = {
  projects_built: 6,
  github_commits: 800,
  leetcode_problems: 50,
  technologies_learned: 20,
  internships: 2,
  gpa: 3.7,
};

function StatMetric({ metric, index, progress, cinematic }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.35 });
  const reduceMotion = useMotionPreference();
  const start = 0.25 + index * 0.075;
  const end = start + 0.12;
  const [countStarted, setCountStarted] = useState(!cinematic);
  const countStartedRef = useRef(!cinematic);
  const opacity = useTransform(progress, [start, end], [0, 1]);
  const y = useTransform(progress, [start, end], [44, 0]);
  const scale = useTransform(progress, [start, end], [0.88, 1]);

  useMotionValueEvent(progress, "change", value => {
    if (cinematic && !countStartedRef.current && value >= start) {
      countStartedRef.current = true;
      setCountStarted(true);
    }
  });
  useEffect(() => {
    if (!cinematic || progress.get() >= start) {
      countStartedRef.current = true;
      setCountStarted(true);
    }
  }, [cinematic, progress, start]);

  return (
    <motion.li
      ref={ref}
      className="flex min-w-0 flex-col items-center text-center"
      style={cinematic ? { opacity, y, scale } : undefined}
      initial={!cinematic && !reduceMotion ? { opacity: 0, y: 24 } : false}
      animate={!cinematic ? (inView || reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }) : undefined}
      transition={{ duration: reduceMotion ? 0 : 0.55, delay: reduceMotion ? 0 : index * 0.07 }}
    >
      <span aria-hidden="true" data-testid={`stat-highlight-${index}`} className="text-brand-500">
        <metric.icon className="h-5 w-5" />
      </span>
      <strong className="mt-5 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl md:text-5xl">
        <Counter to={countStarted ? metric.value : 0} suffix={metric.suffix} decimals={metric.decimals || 0} />
      </strong>
      <span className="mt-2 font-mono text-[10px] uppercase tracking-wider text-white/45 sm:text-xs">{metric.label}</span>
    </motion.li>
  );
}

export default function Stats() {
  const [stats, setStats] = useState(FALLBACK);
  const ref = useRef(null);
  const cinematic = useScrollStage();
  const reduceMotion = useMotionPreference();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end end"] });
  const headingOpacity = useTransform(scrollYProgress, [0.08, 0.22], [0, 1]);
  const headingY = useTransform(scrollYProgress, [0.08, 0.22], [28, 0]);
  const lineProgress = useTransform(scrollYProgress, [0.25, 0.75], [0, 1]);
  const wordX = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const wordOpacity = useTransform(scrollYProgress, [0.04, 0.2, 0.85, 1], [0, 1, 1, 0.55]);

  useEffect(() => {
    getStats().then(setStats).catch(() => {});
  }, []);

  const items = [
    { icon: Rocket, label: "Projects Built", value: stats.projects_built ?? FALLBACK.projects_built, suffix: "+" },
    { icon: GitCommit, label: "GitHub Commits", value: stats.github_commits ?? FALLBACK.github_commits, suffix: "+" },
    { icon: Code2, label: "LeetCode Problems", value: stats.leetcode_problems ?? FALLBACK.leetcode_problems, suffix: "+" },
    { icon: Layers, label: "Technologies Learned", value: stats.technologies_learned ?? FALLBACK.technologies_learned, suffix: "+" },
    { icon: Briefcase, label: "Internships", value: stats.internships ?? FALLBACK.internships, suffix: "" },
    { icon: GraduationCap, label: "Academic GPA", value: stats.gpa ?? FALLBACK.gpa, suffix: " / 4", decimals: 1 },
  ];

  return (
    <section
      ref={ref}
      data-testid="stats"
      aria-labelledby="stats-heading"
      className={cinematic ? "relative h-[190svh]" : "relative mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24"}
    >
      <div className={cinematic ? "sticky top-16 flex h-[calc(100svh-4rem)] items-center overflow-hidden" : ""}>
        {cinematic && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex select-none items-center justify-center overflow-hidden"
          >
            <motion.span
              className="portfolio-backdrop-word relative top-[7vh]"
              style={{ x: wordX, opacity: wordOpacity }}
            >Numbers</motion.span>
          </div>
        )}
        <div className={cinematic ? "relative z-10 mx-auto w-full max-w-7xl px-6 md:px-10" : "relative w-full"}>
          <motion.div style={cinematic ? { opacity: headingOpacity, y: headingY } : undefined}>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-400">The journey so far</p>
            <h2 id="stats-heading" className="mt-3 max-w-4xl font-display text-3xl font-semibold tracking-tight text-white md:text-5xl">
              Work and growth, <span className="text-brand-400">in numbers.</span>
            </h2>
          </motion.div>
          <ul aria-label="Portfolio highlights" className="mt-12 grid grid-cols-2 gap-x-6 gap-y-12 sm:grid-cols-3 lg:mt-16 lg:grid-cols-6 lg:gap-x-8">
            {items.map((metric, index) => (
              <StatMetric key={metric.label} metric={metric} index={index} progress={scrollYProgress} cinematic={cinematic} />
            ))}
          </ul>
          <div aria-hidden="true" className="mt-12 h-px w-full bg-white/10 lg:mt-16">
            <motion.div className="h-full origin-left bg-brand-500/70" style={cinematic ? { scaleX: lineProgress } : undefined} />
          </div>
        </div>
      </div>
    </section>
  );
}
